import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import moment from 'moment';
import { useUpdateDepartureMutation } from '../redux/services/foecheckinApi';
import { ShowToast } from '../Common/common';
import { calculateProjectedDeparture } from '../utils/foActionUtils';

import T from '../Components/T';
interface ExtendStayModalProps {
  visible: boolean;
  room: any;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ExtendStayModal: React.FC<ExtendStayModalProps> = ({
  visible,
  room,
  onClose,
  onSuccess,
}) => {
  const [days, setDays] = useState<number>(1);
  const [updateDeparture, { isLoading }] = useUpdateDepartureMutation();

  useEffect(() => {
    if (visible) {
      setDays(1);
    }
  }, [visible, room]);

  if (!room) return null;

  const currentDep = room.ci_depature ? moment(room.ci_depature) : moment();
  const projectedDep = calculateProjectedDeparture(room.ci_depature, days);

  const handleDaysChange = (text: string) => {
    const val = parseInt(text, 10);
    if (isNaN(val) || val <= 0) {
      setDays(1);
    } else {
      setDays(Math.min(val, 365));
    }
  };

  const handleIncrement = () => {
    setDays(prev => Math.min(prev + 1, 365));
  };

  const handleDecrement = () => {
    setDays(prev => Math.max(prev - 1, 1));
  };

  const handleConfirm = async () => {
    if (!room.ci_id) {
      ShowToast('Check-in record not found for this room', 'error');
      return;
    }

    try {
      await updateDeparture({ id: room.ci_id, days }).unwrap();
      ShowToast(
        `Room ${room.rm_name || ''} Extended For ${days} Day(s)...`,
        'success',
      );
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const msg =
        error?.data?.message ||
        error?.data ||
        error?.message ||
        'Failed to extend stay';
      ShowToast(typeof msg === 'string' ? msg : 'Failed to extend stay', 'error');
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}>
      <View style={modalStyles.overlay}>
        <View style={modalStyles.container}>
          {/* Header */}
          <View style={modalStyles.header}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <FontAwesome name="calendar-plus-o" size={20} color="#0284c7" />
              <Text style={modalStyles.title}><T>Extend Stay</T></Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              disabled={isLoading}
              style={modalStyles.closeButton}>
              <FontAwesome name="times" size={20} color="#6b7280" />
            </TouchableOpacity>
          </View>

          {/* Room & Guest Summary */}
          <View style={modalStyles.infoCard}>
            <View style={modalStyles.infoRow}>
              <Text style={modalStyles.infoLabel}><T>Room:</T></Text>
              <Text style={modalStyles.infoValueBold}>
                {room.rm_name}{' '}
                {room.rt_name ? `(${room.rt_name})` : ''}
              </Text>
            </View>
            {room.ab_name ? (
              <View style={modalStyles.infoRow}>
                <Text style={modalStyles.infoLabel}><T>Guest:</T></Text>
                <Text style={modalStyles.infoValue}>{room.ab_name}</Text>
              </View>
            ) : null}
            <View style={modalStyles.infoRow}>
              <Text style={modalStyles.infoLabel}><T>Current Departure:</T></Text>
              <Text style={modalStyles.infoValue}>
                {currentDep.format('DD-MMM-YYYY hh:mm A')}
              </Text>
            </View>
          </View>

          {/* Stepper Input */}
          <View style={modalStyles.stepperSection}>
            <Text style={modalStyles.stepperLabel}><T>Extend by (Days):</T></Text>
            <View style={modalStyles.stepperRow}>
              <TouchableOpacity
                onPress={handleDecrement}
                disabled={days <= 1 || isLoading}
                style={[
                  modalStyles.stepperBtn,
                  (days <= 1 || isLoading) && modalStyles.stepperBtnDisabled,
                ]}>
                <FontAwesome name="minus" size={16} color={days <= 1 ? '#9ca3af' : '#111827'} />
              </TouchableOpacity>
              <TextInput
                style={modalStyles.stepperInput}
                value={days.toString()}
                onChangeText={handleDaysChange}
                keyboardType="numeric"
                selectTextOnFocus
                editable={!isLoading}
              />
              <TouchableOpacity
                onPress={handleIncrement}
                disabled={isLoading}
                style={modalStyles.stepperBtn}>
                <FontAwesome name="plus" size={16} color="#111827" />
              </TouchableOpacity>
            </View>

            {/* Preset Chips */}
            <View style={modalStyles.chipsRow}>
              {[1, 2, 3, 7].map(preset => (
                <TouchableOpacity
                  key={preset}
                  onPress={() => setDays(preset)}
                  disabled={isLoading}
                  style={[
                    modalStyles.presetChip,
                    days === preset && modalStyles.presetChipActive,
                  ]}>
                  <Text
                    style={[
                      modalStyles.presetChipText,
                      days === preset && modalStyles.presetChipTextActive,
                    ]}>
                    +{preset} {preset === 1 ? 'Day' : 'Days'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Projected Departure Preview */}
          <View style={modalStyles.previewBox}>
            <Text style={modalStyles.previewLabel}><T>New Departure Date & Time:</T></Text>
            <Text style={modalStyles.previewValue}>
              {projectedDep.format('DD-MMM-YYYY hh:mm A')}
            </Text>
          </View>

          {/* Action Buttons */}
          <View style={modalStyles.actionRow}>
            <TouchableOpacity
              onPress={onClose}
              disabled={isLoading}
              style={modalStyles.cancelBtn}>
              <Text style={modalStyles.cancelBtnText}><T>Cancel</T></Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleConfirm}
              disabled={isLoading}
              style={[
                modalStyles.confirmBtn,
                isLoading && modalStyles.confirmBtnDisabled,
              ]}>
              {isLoading ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <>
                  <FontAwesome name="check" size={16} color="#ffffff" style={{ marginRight: 6 }} />
                  <Text style={modalStyles.confirmBtnText}><T>Extend Stay</T></Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  container: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginLeft: 8,
  },
  closeButton: {
    padding: 4,
  },
  infoCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 12,
    marginBottom: 14,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  infoLabel: {
    fontSize: 13,
    color: '#64748b',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 13,
    color: '#1e293b',
  },
  infoValueBold: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: '700',
  },
  stepperSection: {
    marginBottom: 14,
  },
  stepperLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 8,
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  stepperBtn: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#e2e8f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepperBtnDisabled: {
    backgroundColor: '#f1f5f9',
    opacity: 0.6,
  },
  stepperInput: {
    width: 80,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    marginHorizontal: 12,
    textAlign: 'center',
    fontSize: 18,
    fontWeight: '700',
    color: '#0f172a',
    backgroundColor: '#ffffff',
  },
  chipsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  presetChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  presetChipActive: {
    backgroundColor: '#0284c7',
    borderColor: '#0284c7',
  },
  presetChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  presetChipTextActive: {
    color: '#ffffff',
  },
  previewBox: {
    backgroundColor: '#f0f9ff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#bae6fd',
    padding: 12,
    marginBottom: 16,
    alignItems: 'center',
  },
  previewLabel: {
    fontSize: 12,
    color: '#0369a1',
    fontWeight: '500',
    marginBottom: 2,
  },
  previewValue: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0c4a6e',
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  confirmBtn: {
    flexDirection: 'row',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#0284c7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmBtnDisabled: {
    opacity: 0.6,
  },
  confirmBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
});

export default ExtendStayModal;
